import React from "react";
import { Link } from "react-router-dom";
import {
  Facebook,
  Instagram,
  Youtube,
  Phone,
  Mail,
  MapPin,
  MessageCircle,
  ArrowRight,
  Clock3,
} from "lucide-react";
import { siteConfig } from "../../homeComponents/data/siteConfig";
import VisitorCounter from "../VisitorCounter";

const companyLinks = [
  { to: "/", label: "Home" },
  { to: "/tours", label: "Tours & Travels" },
  { to: "/vehicles", label: "Our Vehicles" },
  { to: "/motors", label: "NJV Motors" },
  { to: "/consultancy", label: "Consultancy" },
  { to: "/contact", label: "Contact" },
];

const services = [
  "Car Rental",
  "Coach Rental",
  "Van Rental",
  "Airport Transfers",
  "Outstation Trips",
  "Local Travel",
  "Emergency Service",
];

const socialLinks = [
  {
    href: siteConfig.social.facebook,
    label: "Facebook",
    icon: Facebook,
  },
  {
    href: siteConfig.social.instagram,
    label: "Instagram",
    icon: Instagram,
  },
  {
    href: siteConfig.social.youtube,
    label: "YouTube",
    icon: Youtube,
  },
];

const Footer = () => {
  const whatsappNumber = siteConfig.contact.whatsapp.replace(/\D/g, "");

  return (
    <footer className="relative mt-20 overflow-hidden bg-[#061A3A] text-white">
      {/* Decorative background */}
      <div
        className="
          pointer-events-none absolute -right-32 -top-32
          h-72 w-72 rounded-full
          bg-[#00A260]/10 blur-3xl
        "
      />

      <div
        className="
          pointer-events-none absolute -bottom-40 -left-32
          h-80 w-80 rounded-full
          bg-[#F97316]/10 blur-3xl
        "
      />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* ================= CTA ================= */}
        <div className="border-b border-white/10 py-10 sm:py-14">
          <div
            className="
              overflow-hidden rounded-3xl
              border border-white/10
              bg-gradient-to-br
              from-white/[0.08] to-white/[0.03]
              p-6
              backdrop-blur-sm
              sm:p-8 lg:p-10
            "
          >
            <div className="flex flex-col gap-7 lg:flex-row lg:items-center lg:justify-between">
              <div className="max-w-2xl">
                <span
                  className="
                    inline-flex items-center
                    rounded-full
                    bg-[#00A260]/15
                    px-3 py-1
                    text-[10px]
                    font-bold
                    uppercase
                    tracking-[0.18em]
                    text-[#8BF108]
                  "
                >
                  Travel with confidence
                </span>

                <h2 className="mt-3 text-2xl font-extrabold tracking-tight sm:text-3xl">
                  Ready for your next journey?
                </h2>

                <p className="mt-2 max-w-xl text-sm leading-6 text-white/65 sm:text-base">
                  From local rides to long-distance travel, NJV is here to
                  make your journey comfortable and reliable.
                </p>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row">
                <a
                  href={`tel:${siteConfig.contact.phone}`}
                  className="
                    inline-flex h-12 items-center
                    justify-center gap-2
                    rounded-xl
                    bg-[#00A260]
                    px-5
                    text-sm font-bold
                    text-white
                    shadow-lg
                    shadow-[#00A260]/20
                    transition-all duration-300
                    hover:-translate-y-0.5
                    hover:bg-[#00C878]
                    hover:shadow-xl
                  "
                >
                  <Phone className="h-4 w-4" />
                  Call NJV
                </a>

                <a
                  href={`https://wa.me/${whatsappNumber}`}
                  target="_blank"
                  rel="noreferrer"
                  className="
                    inline-flex h-12 items-center
                    justify-center gap-2
                    rounded-xl
                    border border-white/15
                    bg-white/5
                    px-5
                    text-sm font-bold
                    text-white
                    transition-all duration-300
                    hover:-translate-y-0.5
                    hover:bg-white/10
                  "
                >
                  <MessageCircle className="h-4 w-4" />
                  WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* ================= MAIN FOOTER ================= */}
        <div className="py-12 sm:py-14">
          <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4 lg:gap-12">
            {/* Brand */}
            <div className="sm:col-span-2 lg:col-span-1">
              <Link
                to="/"
                className="inline-flex items-center gap-2"
                aria-label="NJV Home"
              >
                <div
                  className="
                    flex h-11 w-11
                    items-center justify-center
                    rounded-xl
                    bg-[#00A260]
                    text-lg font-black
                    shadow-lg shadow-[#00A260]/20
                  "
                >
                  NJV
                </div>

                <div>
                  <div className="text-lg font-extrabold leading-none">
                    NJV
                  </div>

                  <div className="mt-1 text-[9px] font-bold uppercase tracking-[0.18em] text-white/45">
                    Tours & Travels
                  </div>
                </div>
              </Link>

              <p className="mt-5 max-w-sm text-sm leading-6 text-white/60">
                Comfortable journeys with trusted vehicles. Travel with NJV
                for local rides, airport transfers, outstation trips and more.
              </p>

              <div className="mt-5 flex flex-wrap gap-2">
                <span className="rounded-full bg-white/5 px-3 py-1.5 text-[10px] font-semibold text-white/55">
                  Trusted Service
                </span>

                <span className="rounded-full bg-white/5 px-3 py-1.5 text-[10px] font-semibold text-white/55">
                  24/7 Support
                </span>
              </div>
            </div>

            {/* Company */}
            <div>
              <h3 className="text-xs font-bold uppercase tracking-[0.16em] text-white/90">
                Company
              </h3>

              <ul className="mt-5 space-y-3">
                {companyLinks.map(({ to, label }) => (
                  <li key={to}>
                    <Link
                      to={to}
                      className="
                        group inline-flex items-center
                        gap-1.5
                        text-sm text-white/55
                        transition-colors duration-200
                        hover:text-white
                      "
                    >
                      <span>{label}</span>

                      <ArrowRight
                        className="
                          h-3 w-3
                          -translate-x-1
                          opacity-0
                          transition-all duration-200
                          group-hover:translate-x-0
                          group-hover:opacity-100
                        "
                      />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Services */}
            <div>
              <h3 className="text-xs font-bold uppercase tracking-[0.16em] text-white/90">
                Services
              </h3>

              <ul className="mt-5 space-y-3">
                {services.map((service) => (
                  <li
                    key={service}
                    className="
                      text-sm text-white/55
                      transition-colors duration-200
                      hover:text-white
                    "
                  >
                    {service}
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h3 className="text-xs font-bold uppercase tracking-[0.16em] text-white/90">
                Contact
              </h3>

              <ul className="mt-5 space-y-4">
                <li>
                  <a
                    href={`tel:${siteConfig.contact.phone}`}
                    className="
                      group flex items-start gap-3
                      text-sm text-white/60
                      transition-colors
                      hover:text-white
                    "
                  >
                    <span
                      className="
                        flex h-8 w-8 shrink-0
                        items-center justify-center
                        rounded-lg
                        bg-white/5
                        text-[#8BF108]
                        transition-colors
                        group-hover:bg-[#00A260]/15
                      "
                    >
                      <Phone className="h-4 w-4" />
                    </span>

                    <span className="pt-1">
                      {siteConfig.contact.phone}
                    </span>
                  </a>
                </li>

                <li>
                  <a
                    href={`https://wa.me/${whatsappNumber}`}
                    target="_blank"
                    rel="noreferrer"
                    className="
                      group flex items-start gap-3
                      text-sm text-white/60
                      transition-colors
                      hover:text-white
                    "
                  >
                    <span
                      className="
                        flex h-8 w-8 shrink-0
                        items-center justify-center
                        rounded-lg
                        bg-white/5
                        text-[#8BF108]
                        transition-colors
                        group-hover:bg-[#00A260]/15
                      "
                    >
                      <MessageCircle className="h-4 w-4" />
                    </span>

                    <span className="pt-1">WhatsApp</span>
                  </a>
                </li>

                <li>
                  <a
                    href={`mailto:${siteConfig.contact.email}`}
                    className="
                      group flex items-start gap-3
                      text-sm text-white/60
                      transition-colors
                      hover:text-white
                    "
                  >
                    <span
                      className="
                        flex h-8 w-8 shrink-0
                        items-center justify-center
                        rounded-lg
                        bg-white/5
                        text-[#8BF108]
                        transition-colors
                        group-hover:bg-[#00A260]/15
                      "
                    >
                      <Mail className="h-4 w-4" />
                    </span>

                    <span className="break-all pt-1">
                      {siteConfig.contact.email}
                    </span>
                  </a>
                </li>

                <li>
                  <div className="flex items-start gap-3 text-sm text-white/60">
                    <span
                      className="
                        flex h-8 w-8 shrink-0
                        items-center justify-center
                        rounded-lg
                        bg-white/5
                        text-[#8BF108]
                      "
                    >
                      <MapPin className="h-4 w-4" />
                    </span>

                    <span className="pt-1 leading-5">
                      {siteConfig.contact.address}
                    </span>
                  </div>
                </li>

                <li>
                  <div className="flex items-start gap-3 text-sm text-white/60">
                    <span
                      className="
                        flex h-8 w-8 shrink-0
                        items-center justify-center
                        rounded-lg
                        bg-white/5
                        text-[#8BF108]
                      "
                    >
                      <Clock3 className="h-4 w-4" />
                    </span>

                    <span className="pt-1">
                      Available 24/7
                    </span>
                  </div>
                </li>
              </ul>

              {/* Social */}
              <div className="mt-6 flex items-center gap-2">
                {socialLinks.map(({ href, label, icon: Icon }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={label}
                    className="
                      flex h-9 w-9
                      items-center justify-center
                      rounded-full
                      border border-white/10
                      bg-white/5
                      text-white/65
                      transition-all duration-300
                      hover:-translate-y-0.5
                      hover:border-[#00A260]/40
                      hover:bg-[#00A260]
                      hover:text-white
                    "
                  >
                    <Icon className="h-4 w-4" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ================= BOTTOM BAR ================= */}
        <div
          className="
            flex flex-col gap-4
            border-t border-white/10
            py-6
            text-center
            sm:flex-row
            sm:items-center
            sm:justify-between
            sm:text-left
          "
        >
          <p className="text-[11px] leading-5 text-white/40">
            © 2026 NJV Tours and Travels & NJV Motors.
            <br className="sm:hidden" /> All rights reserved.
          </p>

          <div className="flex items-center justify-center gap-4 text-[11px] text-white/40 sm:justify-end">
            <Link
              to="/"
              className="transition-colors hover:text-white"
            >
              Privacy
            </Link>

            <span className="h-3 w-px bg-white/10" />

            <Link
              to="/"
              className="transition-colors hover:text-white"
            >
              Terms
            </Link>

            <span className="h-3 w-px bg-white/10" />

            <span className="text-[#8BF108]/70">
              NJV
            </span>
          </div>
        </div>
      </div>

         {/* Mobile bottom-navigation spacing */}
      <div className="h-20 md:hidden" />
      
        <div className="border-t border-white/10 px-4 py-5">
        <VisitorCounter />
      </div>
    </footer>
  );
};

export default Footer;